import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

export default function DropdownPortal({
  anchorRef,
  onClose,
  children,
  className,
  align = "left", // 'left' | 'right'
  offset = 0, // additional horizontal offset in px
  topIncrement = 0,
}) {
  const dropdownRef = useRef(null);
  const darkMode = useSelector((state) => state.settings.darkMode);

  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );

  useEffect(() => {
    const button = anchorRef.current;
    if (!button) return;

    const updatePosition = () => {
      const rect = button.getBoundingClientRect();
      if (!dropdownRef.current) return;

      // Prepare dropdown for measurement
      dropdownRef.current.style.position = "fixed";
      dropdownRef.current.style.width = `${rect.width}px`;
      dropdownRef.current.style.visibility = "hidden";
      dropdownRef.current.style.top = `0px`;

      // Compute horizontal position first
      const dropdownWidth = dropdownRef.current.offsetWidth || rect.width;
      let computedLeft = rect.left;
      if (align === "right") {
        computedLeft = rect.right - dropdownWidth;
      }
      computedLeft = Math.round(computedLeft + offset);

      // Constrain horizontally within viewport
      const margin = 20;
      if (computedLeft + dropdownWidth > window.innerWidth - margin) {
        computedLeft = window.innerWidth - dropdownWidth - margin;
      }
      if (computedLeft < margin) computedLeft = margin;

      // Measure dropdown height after width/left applied
      dropdownRef.current.style.left = `${computedLeft}px`;
      // Force reflow to ensure offsetHeight is available
      const dropdownHeight = dropdownRef.current.offsetHeight || dropdownRef.current.getBoundingClientRect().height || 0;

      // Decide whether to open below or above based on available space
      const spaceBelow = window.innerHeight - rect.bottom - 4 - topIncrement;
      const spaceAbove = rect.top - 4 - topIncrement;
      let computedTop;
      if (dropdownHeight === 0) {
        // If height couldn't be measured, default to below
        computedTop = rect.bottom + 4 + topIncrement;
      } else if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        // Not enough space below, but enough above -> open above
        computedTop = rect.top - dropdownHeight - 4 - topIncrement;
      } else {
        // Default: open below
        computedTop = rect.bottom + 4 + topIncrement;
      }

      // Ensure top stays within viewport margins
      if (computedTop < margin) computedTop = margin;

      dropdownRef.current.style.top = `${Math.round(computedTop)}px`;
      dropdownRef.current.style.zIndex = 9999;
      dropdownRef.current.style.visibility = "visible";
    };

    // Initial position
    updatePosition();

    // Update on scroll + resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    // Close when clicking outside
    const handleClickOutside = (e) => {
      if (
        !dropdownRef.current?.contains(e.target) &&
        !button.contains(e.target)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorRef, defaultLanguage, align, offset, topIncrement]);

  const dropdownClasses = darkMode
    ? "bg-gray-800 border border-gray-700 rounded-md shadow-lg"
    : "bg-white border border-gray-300 rounded-md shadow-lg";

  return createPortal(
    <div ref={dropdownRef} className={`${className || ""} ${dropdownClasses}`}>
      {children}
    </div>,
    document.body
  );
}
